import { useState, useEffect, useCallback } from 'react'
import { Modal, Button, TextHeader, Checkbox } from '@eltoro-ui/components'
import { exportCSVColumnsType, possibleColumns } from './columns'
import { unparse } from 'papaparse'
import {
  Campaignservicev1Campaign,
  Campaignservicev1OrderLine,
} from 'next-gen-sdk'
import { downloadBlob } from 'Helpers/BuildAndDownloadCSV'
import dayjs from 'dayjs'

export type ExportModalProps = {
  isOpen?: boolean
  tableData:
    | Partial<Campaignservicev1OrderLine[]>
    | Partial<Campaignservicev1Campaign[]>
    | undefined
  includeChild: boolean
  view: 'orderLine' | 'campaign'
  onClose: () => void
  columnsOverride?: exportCSVColumnsType[] | null
}

export const ReportExportModal = ({
  isOpen = false,
  tableData,
  view,
  includeChild,
  onClose,
  columnsOverride = null,
}: ExportModalProps) => {
  //Manages which subset of columns to render based on state conditions
  const selectColumn = useCallback(() => {
    if (!includeChild) {
      if (view === 'orderLine') return possibleColumns.orderlines
      return possibleColumns.campaigns
    } else {
      if (view === 'orderLine') return possibleColumns.orderLinesWChild
      return possibleColumns.campaignsWChild
    }
  }, [includeChild, view])

  // State for managing avaliable columns, columns can be overridden with a prop
  const [columns, setColumnsData] = useState<exportCSVColumnsType[]>(
    columnsOverride ? columnsOverride : selectColumn(),
  )

  //Update the avaliable column if conditions change
  useEffect(
    () => setColumnsData(columnsOverride ? columnsOverride : selectColumn()),
    [columnsOverride, includeChild, view, selectColumn],
  )
  const downloadCSV = () => {
    if (!tableData) return console.log('No table data avaliable to export')
    //Filter out only included columns
    const finalColumns = columns.filter((col) => col.included)

    //Format the data based on the table data provided
    const formattedData = formatData(finalColumns, tableData)

    //Convert from JSON -> CSV
    const csvContent = unparse({
      fields: finalColumns.map((column) => column.col),
      data: formattedData,
    })

    // Clean dayjs timestamp format for El Toro ReportHub exports
    const fileTimestamp = dayjs().format('YYYY_MM_DD_hh_mm_ss_A')

    //Download the csv file
    downloadBlob(
      csvContent,
      `ElToro_AdvancedReporting_${view.toUpperCase()}_Export_${fileTimestamp}`,
      'text/csv;charset=utf-8;',
    )
  }
  const formatData = (
    columns: exportCSVColumnsType[],
    data:
      | Partial<Campaignservicev1OrderLine[]>
      | Partial<Campaignservicev1Campaign[]>,
  ) => {
    /*
    Iterate through the data using column definitions. If a column provides
    a getValue override, use it; otherwise, resolve the value from an optional
    dot-path on the row.
    */
    const resolvePath = (obj: any, path?: string): any => {
      if (!path) return undefined
      return path.split('.').reduce((acc, key) => {
        if (acc == null) return undefined
        return acc[key]
      }, obj)
    }

    return data.map((row: any) => {
      const rowObject: Record<string, string> = {}
      columns.forEach((column) => {
        const computed = column.getValue ? column.getValue(row) : undefined
        const raw =
          computed !== undefined && computed !== null
            ? computed
            : resolvePath(row, column.path)
        rowObject[column.col] =
          raw !== undefined && raw !== null ? String(raw) : ''
      })
      return rowObject
    })
  }

  if (!isOpen) return null

  return (
    <Modal className="ReportExportModal__modal-container" offClick={onClose}>
      {tableData ? (
        <>
          <TextHeader className="ReportExportModal__title-header" type={5}>
            Select Columns for Export
          </TextHeader>
          <div className="ReportExportModal_checkbox-container pt-2 pb-2">
            {columns.map((column, index) => {
              return (
                <div key={column.col} className="mb-2">
                  <Checkbox
                    label={column.col}
                    checked={column.included}
                    onChange={() => {
                      setColumnsData((prev) =>
                        prev.map((col, idx) =>
                          idx === index
                            ? { ...col, included: !col.included }
                            : col,
                        ),
                      )
                    }}
                  />
                </div>
              )
            })}

            <footer className="ReportExportModal__buttons mt-6 flex justify-end gap-2">
              <Button onClick={onClose}>Close</Button>
              <Button
                kind="primary"
                onClick={() => {
                  downloadCSV()
                  onClose()
                }}
              >
                Download
              </Button>
            </footer>
          </div>
        </>
      ) : (
        <div className="ReportExportModal_no-table-data text-lg font-bold">
          <TextHeader className="ReportExportModal__no-data-header" type={5}>
            Unable to Load Table Data for Export
          </TextHeader>
          <div className="mt-4 flex justify-center">
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      )}
    </Modal>
  )
}

export default ReportExportModal
